import { Component, OnInit, Inject, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { AuthService } from './auth.service';
import { ProyectosService, ProyectoListItem } from './proyectos.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  isDarkMode = true;
  currentRoute = '';
  isAuthenticated = false;
  userName: string | null = null;
  userEmail: string | null = null;
  isSidebarOpen = true; // Sidebar abierto por defecto en desktop
  showNewProjectModal = false;
  proyectos: ProyectoListItem[] = [];
  proyectoActivoId: string | null = null;
  
  // Edición inline de proyecto
  editingProyectoId: string | null = null;
  editingProyectoName: string = '';
  isRenaming = false;
  
  // Proyectos expandidos y sus elementos
  expandedProyectos: Set<string> = new Set();
  proyectoElementos: Map<string, any[]> = new Map();
  loadingProyectoElementos: Set<string> = new Set();
  
  // Colores de los proyectos (proyectoId -> color)
  proyectoColores: Map<string, string> = new Map();
  
  // Elemento seleccionado en el árbol (proyectoId, índice)
  selectedElementInTree: { proyectoId: string; index: number } | null = null;
  
  // Mis Imágenes
  misImagenes: Array<{ id: number; UsuarioId: string; url_image: string }> = [];
  loadingImagenes = false;
  
  // Redimensionamiento del sidebar
  sidebarWidth = 256; // Ancho por defecto
  isResizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  
  // Menú móvil para usuarios no autenticados
  isMobileMenuOpen = false;
  
  constructor(
    private router: Router,
    private msalService: MsalService,
    private authService: AuthService,
    private proyectosService: ProyectosService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {
    // Observar cambios de ruta
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.currentRoute = event.url;
      // Verificar estado de autenticación en cada cambio de ruta
      // Esto es importante para detectar cuando el usuario hace login con email
      this.checkAuthState();
      // Cerrar sidebar en móvil después de navegar
      if (window.innerWidth <= 1024) {
        this.isSidebarOpen = false;
      }
      // Detectar proyecto activo desde query params
      const urlTree = this.router.parseUrl(event.url);
      const proyectoIdParam = urlTree.queryParams['proyectoId'];
      if (proyectoIdParam && event.url.includes('/banner-editor')) {
        this.proyectoActivoId = proyectoIdParam;
      } else if (!event.url.includes('/banner-editor')) {
        this.proyectoActivoId = null;
      }
      // Recargar proyectos e imágenes periódicamente cuando estamos autenticados
      if (this.isAuthenticated) {
        this.cargarProyectos();
        this.cargarMisImagenes();
      }
    });
    
    // Escuchar eventos de actualización de elementos del proyecto
    window.addEventListener('projectElementsUpdated', (event: any) => {
      const detail = event.detail;
      if (detail && detail.proyectoId) {
        this.updateProyectoElements(detail.proyectoId, detail.elements);
      }
    });
    
    // Escuchar eventos de actualización de selección desde el canvas
    window.addEventListener('updateElementSelectionInTree', this.handleUpdateElementSelectionInTree.bind(this));
    window.addEventListener('clearElementSelectionInTree', this.handleClearElementSelectionInTree.bind(this));
    
    // Escuchar eventos de actualización de imágenes
    window.addEventListener('imagesUpdated', () => {
      if (this.isAuthenticated) {
        this.cargarMisImagenes();
      }
    });
  }
  
  ngOnInit() {
    // Cargar ancho del sidebar desde localStorage
    const savedWidth = localStorage.getItem('sidebarWidth');
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      if (width >= 200 && width <= 600) {
        this.sidebarWidth = width;
      }
    }
    
    // Inicializar tema al cargar - asegurar que body tenga la clase
    if (!document.body.classList.contains('dark-theme') && !document.body.classList.contains('light-theme')) {
      document.body.classList.add('dark-theme');
    }
    this.applyTheme();
    this.currentRoute = this.router.url;
    
    // Ajustar sidebar según tamaño de pantalla
    this.adjustSidebarForScreenSize();
    window.addEventListener('resize', () => this.adjustSidebarForScreenSize());
    
    // Event listeners para redimensionamiento del sidebar
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
    
    // Manejar el redirect de MSAL después del login
    this.msalService.instance.handleRedirectPromise().then((response) => {
      if (response && response.account) {
        this.msalService.instance.setActiveAccount(response.account);
        this.checkAuthState();
      } else {
        // Verificar si ya hay una cuenta guardada
        this.checkAuthState();
      }
    }).catch((error) => {
      console.error('Error handling redirect:', error);
      this.checkAuthState();
    });
    
    // Suscribirse a cambios en la autenticación
    this.msalService.instance.addEventCallback((event: any) => {
      if (event.eventType === 'msal:loginSuccess' || event.eventType === 'msal:acquireTokenSuccess') {
        this.checkAuthState();
        // Sincronizar usuario después del login exitoso
        if (event.eventType === 'msal:loginSuccess') {
          this.syncUserToDatabase();
        }
      }
      if (event.eventType === 'msal:logoutSuccess') {
        this.isAuthenticated = false;
        this.userName = null;
        this.userEmail = null;
      }
    });
  }
  
  private checkAuthState() {
    this.isAuthenticated = this.authService.isAuthenticated();
    if (this.isAuthenticated) {
      this.userName = this.authService.getUserName();
      this.userEmail = this.authService.getUserEmail();
      
      // Solo sincronizar con MSAL si es autenticación con Microsoft
      const authMethod = localStorage.getItem('authMethod');
      if (authMethod !== 'email') {
        // Establecer la cuenta activa de MSAL
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.msalService.instance.setActiveAccount(accounts[0]);
        }
        // Sincronizar usuario con la base de datos (solo para MSAL)
        this.syncUserToDatabase();
      }
      
      // Cargar proyectos e imágenes del usuario
      this.cargarProyectos();
      this.cargarMisImagenes();
    } else {
      this.userName = null;
      this.userEmail = null;
      this.proyectos = [];
      this.proyectoActivoId = null;
      this.misImagenes = [];
    }
  }

  private cargarProyectos() {
    const userId = this.authService.getUserId();
    if (userId) {
      this.proyectosService.getProyectos(userId).subscribe({
        next: (proyectos) => {
          this.proyectos = proyectos;
        },
        error: (error) => {
          console.error('Error cargando proyectos:', error);
        }
      });
    }
  }

  private cargarMisImagenes() {
    const userId = this.authService.getUserId();
    if (userId) {
      this.loadingImagenes = true;
      this.proyectosService.getImagenes(userId).subscribe({
        next: (response) => {
          this.misImagenes = response.imagenes || [];
          this.loadingImagenes = false;
        },
        error: (error) => {
          console.error('Error cargando imágenes:', error);
          this.loadingImagenes = false;
        }
      });
    }
  }

  getImageName(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop() || 'imagen';
      // Remover timestamp si existe (formato: nombre_timestamp.ext)
      return fileName.replace(/_\d+\./, '.');
    } catch {
      return 'imagen';
    }
  }

  abrirImagen(imagen: { id: number; UsuarioId: string; url_image: string }) {
    this.router.navigate(['/images'], { queryParams: { imagenId: imagen.id } });
    // Cerrar sidebar en móvil
    if (window.innerWidth <= 1024) {
      this.isSidebarOpen = false;
    }
  }

  private syncUserToDatabase() {
    // Sincronizar usuario después de un pequeño delay para asegurar que todo esté inicializado
    setTimeout(() => {
      this.authService.syncUser().subscribe({
        next: (result) => {
          if (result.action === 'created') {
            console.log('Usuario registrado en la base de datos');
          } else if (result.action === 'updated') {
            console.log('Usuario actualizado en la base de datos');
          } else if (result.error) {
            console.error('Error sincronizando usuario:', result.error);
          }
        },
        error: (error) => {
          console.error('Error en la sincronización del usuario:', error);
        }
      });
    }, 500);
  }

  getInitials(): string {
    if (this.userName) {
      const parts = this.userName.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return this.userName.substring(0, 2).toUpperCase();
    }
    if (this.userEmail) {
      return this.userEmail.substring(0, 2).toUpperCase();
    }
    return 'U';
  }
  
  toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme();
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  // Redimensionamiento del sidebar
  onResizeStart(event: MouseEvent) {
    event.preventDefault();
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isResizing) return;
    
    event.preventDefault();
    const deltaX = event.clientX - this.resizeStartX;
    const newWidth = this.resizeStartWidth + deltaX;
    
    // Limitar el ancho entre 200px y 600px
    if (newWidth >= 200 && newWidth <= 600) {
      this.sidebarWidth = newWidth;
    }
  }

  onMouseUp(event: MouseEvent) {
    if (!this.isResizing) return;
    
    this.isResizing = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    
    // Guardar el ancho en localStorage
    localStorage.setItem('sidebarWidth', this.sidebarWidth.toString());
  }
  
  private adjustSidebarForScreenSize() {
    // En desktop (ancho > 1024px), sidebar siempre abierto
    // En móvil/tablet, sidebar cerrado por defecto
    if (window.innerWidth > 1024) {
      this.isSidebarOpen = true;
    } else {
      this.isSidebarOpen = false;
    }
  }
  
  private applyTheme() {
    // Aplicar clase al body para el tema
    if (this.isDarkMode) {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
  }
  
  isActiveRoute(route: string): boolean {
    if (route === '/banner-editor' && this.proyectoActivoId) {
      return this.currentRoute.startsWith('/banner-editor');
    }
    return this.currentRoute === route;
  }
  
  goToHome() {
    this.router.navigate(['/home']);
    // Cerrar sidebar en móvil después de navegar
    if (window.innerWidth <= 1024) {
      this.isSidebarOpen = false;
    }
  }
  
  goToBannerEditor() {
    // Navegar sin proyectoId para edición temporal (sin guardar en BD)
    this.router.navigate(['/banner-editor'], { queryParams: {} });
    this.proyectoActivoId = null; // No hay proyecto activo
    // Cerrar sidebar en móvil después de navegar
    if (window.innerWidth <= 1024) {
      this.isSidebarOpen = false;
    }
  }

  goToImages() {
    this.router.navigate(['/images']);
    // Cerrar sidebar en móvil
    if (window.innerWidth <= 1024) {
      this.isSidebarOpen = false;
    }
  }
  
  goToPricing() {
    this.router.navigate(['/pricing']);
  }

  goToPrivacy() {
    // Navegar a la página de aviso de privacidad
    this.router.navigate(['/privacy']);
  }

  goToSupport() {
    // Navegar a la página de soporte
    this.router.navigate(['/support']);
  }

  goToCasosUso() {
    // Navegar a la página de casos de uso
    this.router.navigate(['/casos-uso']);
    // Cerrar sidebar en móvil después de navegar (solo si está autenticado)
    if (this.isAuthenticated && window.innerWidth <= 1024) {
      this.isSidebarOpen = false;
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }
  
  login() {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect({
        scopes: ['user.read']
      });
    }
  }
  
  logout() {
    this.authService.logout();
  }

  crearProyectoBanner() {
    const userId = this.authService.getUserId();
    if (!userId) {
      console.error('Usuario no autenticado');
      return;
    }

    // Generar nombre con fecha y segundos
    const now = new Date();
    const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
    const nombre = `banner_${timestamp}`;

    // Datos iniciales del proyecto
    const datosProyecto = {
      canvasWidth: 1000,
      canvasHeight: 400,
      backgroundColor: '#FFFFFF',
      elements: []
    };

    this.proyectosService.createProyecto({
      usuarioId: userId,
      nombre: nombre,
      tipo: 'banner',
      datosProyecto: datosProyecto
    }).subscribe({
      next: (proyecto) => {
        console.log('Proyecto creado exitosamente:', proyecto);
        
        if (!proyecto || !proyecto.Id) {
          console.error('Respuesta del servidor inválida:', proyecto);
          alert('Error: Respuesta del servidor inválida. Por favor intenta nuevamente.');
          return;
        }

        this.showNewProjectModal = false;
        
        // Convertir Id a string si es necesario
        const proyectoId = proyecto.Id.toString ? proyecto.Id.toString() : String(proyecto.Id);
        
        // Agregar a la lista y navegar al editor
        this.proyectos.unshift({
          Id: proyectoId,
          Nombre: proyecto.Nombre || nombre,
          Tipo: proyecto.Tipo || 'banner',
          FechaCreacion: proyecto.FechaCreacion || new Date().toISOString(),
          FechaModificacion: proyecto.FechaCreacion || new Date().toISOString()
        });
        
        // Navegar al editor con el ID del proyecto
        this.router.navigate(['/banner-editor'], { queryParams: { proyectoId: proyectoId } });
        
        // Cerrar sidebar en móvil
        if (window.innerWidth <= 1024) {
          this.isSidebarOpen = false;
        }
      },
      error: (error) => {
        console.error('Error creando proyecto:', error);
        console.error('Detalles del error:', {
          status: error.status,
          message: error.message,
          error: error.error
        });
        alert('Error al crear el proyecto. Por favor intenta nuevamente.');
      }
    });
  }

  crearProyectoProcesador() {
    // Por ahora solo muestra mensaje
    alert('Esta funcionalidad estará disponible próximamente.');
  }

  abrirProyecto(proyecto: ProyectoListItem) {
    this.proyectoActivoId = proyecto.Id;
    
    // Expandir automáticamente el proyecto si no está expandido
    if (!this.expandedProyectos.has(proyecto.Id)) {
      this.expandedProyectos.add(proyecto.Id);
      // Cargar elementos si no están cargados
      if (!this.proyectoElementos.has(proyecto.Id)) {
        this.cargarElementosProyecto(proyecto);
      }
    }
    
    if (proyecto.Tipo === 'banner') {
      this.router.navigate(['/banner-editor'], { queryParams: { proyectoId: proyecto.Id } });
    }
    // Cerrar sidebar en móvil
    if (window.innerWidth <= 1024) {
      this.isSidebarOpen = false;
    }
  }
  
  toggleProyectoExpand(proyecto: ProyectoListItem, event: Event) {
    event.stopPropagation();
    
    if (this.expandedProyectos.has(proyecto.Id)) {
      // Colapsar
      this.expandedProyectos.delete(proyecto.Id);
    } else {
      // Expandir - cargar elementos si no están cargados
      this.expandedProyectos.add(proyecto.Id);
      if (!this.proyectoElementos.has(proyecto.Id)) {
        this.cargarElementosProyecto(proyecto);
      }
    }
  }
  
  cargarElementosProyecto(proyecto: ProyectoListItem) {
    if (this.loadingProyectoElementos.has(proyecto.Id)) {
      return; // Ya se está cargando
    }
    
    this.loadingProyectoElementos.add(proyecto.Id);
    
    this.proyectosService.getProyecto(proyecto.Id).subscribe({
      next: (proyectoCompleto) => {
        const elementos = proyectoCompleto.DatosProyecto?.elements || [];
        this.proyectoElementos.set(proyecto.Id, elementos);
        // Guardar el color del proyecto si existe
        if (proyectoCompleto.DatosProyecto?.projectColor) {
          this.proyectoColores.set(proyecto.Id, proyectoCompleto.DatosProyecto.projectColor);
        }
        this.loadingProyectoElementos.delete(proyecto.Id);
      },
      error: (error) => {
        console.error('Error cargando elementos del proyecto:', error);
        this.proyectoElementos.set(proyecto.Id, []);
        this.loadingProyectoElementos.delete(proyecto.Id);
      }
    });
  }
  
  isProyectoExpanded(proyectoId: string): boolean {
    return this.expandedProyectos.has(proyectoId);
  }
  
  getProyectoElementos(proyectoId: string): any[] {
    return this.proyectoElementos.get(proyectoId) || [];
  }
  
  getElementoIcon(elemento: any): string {
    switch (elemento.type) {
      case 'text':
        return 'text';
      case 'image':
        return 'image';
      case 'shape':
        return 'shape';
      default:
        return 'circle';
    }
  }
  
  getElementoNombre(elemento: any): string {
    // Usar el nombre que ya viene del banner editor (que incluye el número de índice)
    // Esto asegura que los nombres coincidan exactamente entre el panel de elementos y el árbol
    if (elemento.name) {
      return elemento.name;
    }
    
    // Fallback por si no viene el nombre (compatibilidad hacia atrás)
    if (elemento.type === 'text') {
      return elemento.text || 'Texto';
    } else if (elemento.type === 'image') {
      return 'Imagen';
    } else if (elemento.type === 'shape') {
      return elemento.shapeType === 'rect' ? 'Rectángulo' :
             elemento.shapeType === 'circle' ? 'Círculo' :
             elemento.shapeType === 'triangle' ? 'Triángulo' :
             elemento.shapeType === 'ellipse' ? 'Elipse' :
             elemento.shapeType === 'line' ? 'Línea' : 'Forma';
    }
    return 'Elemento';
  }
  
  isLoadingElementos(proyectoId: string): boolean {
    return this.loadingProyectoElementos.has(proyectoId);
  }
  
  // Obtener el color del proyecto
  getProjectColor(proyecto: ProyectoListItem): string {
    // Buscar el color en el mapa de colores
    const color = this.proyectoColores.get(proyecto.Id);
    if (color) {
      return color;
    }
    // Si no está cargado, cargarlo de forma lazy
    if (!this.proyectoColores.has(proyecto.Id)) {
      this.cargarColorProyecto(proyecto);
    }
    return '#3b82f6'; // Color por defecto mientras se carga
  }
  
  // Cargar el color del proyecto de forma lazy
  private cargarColorProyecto(proyecto: ProyectoListItem) {
    // Marcar como cargando para evitar múltiples llamadas
    this.proyectoColores.set(proyecto.Id, '#3b82f6'); // Temporal
    
    this.proyectosService.getProyecto(proyecto.Id).subscribe({
      next: (proyectoCompleto) => {
        const color = proyectoCompleto.DatosProyecto?.projectColor || '#3b82f6';
        this.proyectoColores.set(proyecto.Id, color);
      },
      error: (error) => {
        console.error('Error cargando color del proyecto:', error);
        // Mantener el color por defecto
      }
    });
  }
  
  // Actualizar elementos de un proyecto sin recargar desde el servidor
  updateProyectoElements(proyectoId: string, elements: any[]) {
    this.proyectoElementos.set(proyectoId, elements);
    // Si el proyecto está expandido, los cambios se reflejarán automáticamente
  }
  
  // Seleccionar elemento desde el árbol
  selectElementFromTree(proyectoId: string, index: number, elementName?: string) {
    // Solo seleccionar si estamos en el editor de banner
    // Verificar si la ruta incluye banner-editor (puede tener query params)
    const isBannerEditor = this.currentRoute.includes('/banner-editor');
    
    if (isBannerEditor) {
      this.selectedElementInTree = { proyectoId, index };
      
      // Emitir evento personalizado para que el banner-editor seleccione el elemento
      const event = new CustomEvent('selectElementFromTree', {
        detail: {
          proyectoId: proyectoId,
          elementIndex: index,
          elementName: elementName
        }
      });
      window.dispatchEvent(event);
    }
  }
  
  // Verificar si un elemento está seleccionado
  isElementSelected(proyectoId: string, index: number): boolean {
    return this.selectedElementInTree?.proyectoId === proyectoId && 
           this.selectedElementInTree?.index === index;
  }
  
  // Manejar actualización de selección desde el canvas
  private handleUpdateElementSelectionInTree(event: any) {
    const { proyectoId, elementIndex } = event.detail;
    this.selectedElementInTree = { proyectoId, index: elementIndex };
  }
  
  // Manejar limpieza de selección desde el canvas
  private handleClearElementSelectionInTree(event: any) {
    const { proyectoId } = event.detail;
    if (this.selectedElementInTree?.proyectoId === proyectoId) {
      this.selectedElementInTree = null;
    }
  }
  
  // Interceptar navegación para verificar cambios no guardados
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    // El banner-editor manejará su propio beforeunload
    // Este es solo para cambios globales si los hay
  }

  // Renombrar proyecto (edición inline)
  startEditingProyecto(proyecto: ProyectoListItem) {
    this.editingProyectoId = proyecto.Id;
    this.editingProyectoName = proyecto.Nombre;
  }

  cancelEditingProyecto() {
    this.editingProyectoId = null;
    this.editingProyectoName = '';
    this.isRenaming = false;
  }

  saveProyectoName() {
    if (!this.editingProyectoId) {
      return;
    }

    const trimmedName = this.editingProyectoName ? this.editingProyectoName.trim() : '';
    if (!trimmedName || trimmedName.length === 0) {
      alert('Por favor, ingresa un nombre válido para el proyecto');
      return;
    }

    this.isRenaming = true;

    this.proyectosService.updateProyecto(this.editingProyectoId, {
      nombre: trimmedName
    }).subscribe({
      next: (result) => {
        console.log('Proyecto renombrado exitosamente:', result);
        this.isRenaming = false;
        
        // Actualizar el nombre en la lista local
        const proyectoIndex = this.proyectos.findIndex(p => p.Id === this.editingProyectoId);
        if (proyectoIndex !== -1) {
          this.proyectos[proyectoIndex].Nombre = trimmedName;
        }
        
        // Salir del modo de edición
        this.cancelEditingProyecto();
        
        // Recargar la lista de proyectos para asegurar sincronización con la BD
        this.cargarProyectos();
      },
      error: (error) => {
        console.error('Error renombrando proyecto:', error);
        this.isRenaming = false;
        alert('Error al renombrar el proyecto. Por favor intenta nuevamente.');
      }
    });
  }

  isEditingProyecto(proyectoId: string): boolean {
    return this.editingProyectoId === proyectoId;
  }

  // Eliminar proyecto
  deleteProyecto(proyecto: ProyectoListItem) {
    const confirmMessage = `¿Estás seguro de que deseas eliminar el proyecto "${proyecto.Nombre}"? Esta acción no se puede deshacer.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    this.proyectosService.deleteProyecto(proyecto.Id).subscribe({
      next: () => {
        console.log('Proyecto eliminado exitosamente (activo = 0)');
        
        // Si el proyecto eliminado era el activo, limpiar la referencia
        if (this.proyectoActivoId === proyecto.Id) {
          this.proyectoActivoId = null;
          // Si estamos en el editor de banner, redirigir a home
          if (this.currentRoute.includes('/banner-editor')) {
            this.router.navigate(['/home']);
          }
        }
        
        // Recargar la lista de proyectos desde la BD (solo mostrará activo = 1)
        this.cargarProyectos();
      },
      error: (error) => {
        console.error('Error eliminando proyecto:', error);
        alert('Error al eliminar el proyecto. Por favor intenta nuevamente.');
      }
    });
  }
}
